'use client';

import { trackEvent } from '@intercom/messenger-js-sdk';
import { directApiClient } from '@/lib/api/direct-client';
import type {
  GuidanceEventName,
  GuidanceEventProperties,
  GuidanceEventPropertyValue
} from './events';

/** The audited endpoint whose `api_audit_logs` row is the server-side trace. */
const GUIDANCE_EVENT_ENDPOINT = '/profile/guidance-event';

type IntercomFn = (command: string, ...args: unknown[]) => void;

/**
 * Whether the Messenger has actually booted.
 *
 * Read off `window.Intercom` rather than the intercom-store, because this is
 * called from event handlers and effects outside React's tree as often as
 * inside it, and the store's hook form is not available there. The SDK's own
 * `trackEvent` already no-ops with a console warning when the Messenger is
 * absent; checking first keeps the console clean on the pages where the
 * Messenger is deliberately not mounted (unauthenticated routes).
 */
function messengerReady(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof (window as { Intercom?: IntercomFn }).Intercom === 'function'
  );
}

/**
 * Record that a guidance surface was used.
 *
 * **Both halves, one call, and neither can hold up the user.** The Intercom
 * event is what lets a segment, tour audience or checklist rule target the
 * behaviour; the POST leaves the `api_audit_logs` row that makes the count ours
 * without opening the Intercom UI. See `events.ts` for why both are
 * needed.
 *
 * **Fire-and-forget, and it never throws.** A guidance impression is worth far
 * less than whatever the user was actually doing, so every failure is swallowed:
 * a failed POST, a Messenger that never booted, a browser blocking the Intercom
 * script. The initiative's own constraint is that impressions must not cost page
 * performance, and a surface that awaited its own telemetry would be the way
 * that breaks. Callers must not `await` this for correctness; `void` it.
 *
 * Property names are typed from the event's registry entry, so naming one the
 * event does not declare is a compile error here rather than a 400 that is
 * invisible on a call nobody reads the response of.
 */
export function trackGuidanceEvent<E extends GuidanceEventName>(
  event: E,
  properties?: GuidanceEventProperties<E>
): void {
  const payload = properties as
    | Record<string, GuidanceEventPropertyValue>
    | undefined;

  try {
    if (messengerReady()) {
      // Intercom's own signature is (name, metadata); metadata is optional.
      if (payload && Object.keys(payload).length > 0) {
        trackEvent(event, payload);
      } else {
        trackEvent(event);
      }
    }
  } catch {
    // An Intercom failure must not cost the server-side trace below.
  }

  /*
    The try/catch is not redundant with the `.catch()`. `post` can throw
    synchronously before it ever returns a promise (a transport that is not
    there, an auth helper that raises while assembling headers), and such a
    throw would escape into the click handler or effect that called this,
    which is the one thing this function promises never to do.
  */
  try {
    void directApiClient
      .post<void>(GUIDANCE_EVENT_ENDPOINT, { event, properties: payload })
      .catch(() => {
        // Intentionally silent. See the doc comment above.
      });
  } catch {
    // The same reason, one layer out.
  }
}
